import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Building2, Loader2, ArrowLeft, CheckCircle2 } from "lucide-react";
import { formatApiErrorDetail } from "@/components/schulungen/roles";
import { trackConversion } from "@/lib/adsConversion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { firmenname: "", ansprechpartner: "", email: "", telefon: "", strasse: "", plz: "", ort: "", passwort: "", passwort2: "", agb_akzeptiert: false };

function Field({ id, label, required, children }) {
  return (
    <div>
      <Label htmlFor={id} className="text-sm font-semibold text-slate-700 mb-1.5 block">{label}{required && <span className="text-red-500"> *</span>}</Label>
      {children}
    </div>
  );
}

export default function Firmenregistrierung() {
  const { setSession } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); document.title = "Firma registrieren | Schulungsportal | Lehnen & Weinacht"; }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.passwort.length < 8) return setError("Das Passwort muss mindestens 8 Zeichen lang sein.");
    if (form.passwort !== form.passwort2) return setError("Die Passwörter stimmen nicht überein.");
    if (!form.agb_akzeptiert) return setError("Bitte akzeptieren Sie die Teilnahmebedingungen.");
    setLoading(true);
    try {
      const { passwort2, ...payload } = form;
      const { data } = await axios.post(`${API}/schulungen/firmen/registrieren`, payload, { withCredentials: true });
      trackConversion("firmenregistrierung", { transactionId: data?.firma_id });
      setSession(data);
      navigate("/schulungen/firma", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail, "Registrierung fehlgeschlagen"));
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4" data-testid="firmenregistrierung-page">
      <div className="max-w-5xl mx-auto grid lg:grid-cols-5 gap-8">
        <aside className="lg:col-span-2">
          <Link to="/schulungen" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-cyan-700 mb-6"><ArrowLeft className="h-4 w-4" /> Zurück zur Schulungsseite</Link>
          <div className="h-14 w-14 rounded-2xl bg-cyan-700 text-white flex items-center justify-center mb-5"><Building2 className="h-7 w-7" /></div>
          <h1 className="font-['Manrope'] text-3xl sm:text-4xl font-bold text-slate-900 leading-tight">Firmenzugang anlegen</h1>
          <p className="mt-4 text-slate-600 leading-relaxed">Kostenlos und unverbindlich. Nach der Registrierung können Sie sofort Schulungsplätze auf Rechnung bestellen.</p>
          <ul className="mt-8 space-y-3 text-sm text-slate-700">
            {["Keine Kreditkarte nötig – Zahlung auf Rechnung", "Plätze sofort verfügbar – Zertifikate nach Zahlungseingang per Post", "Mitarbeitende per Einladungslink hinzufügen", "Fortschritt & Zertifikate im Firmenportal einsehen"].map(t => (
              <li key={t} className="flex items-start gap-2.5"><CheckCircle2 className="h-5 w-5 text-green-600 shrink-0" />{t}</li>
            ))}
          </ul>
        </aside>

        <div className="lg:col-span-3 bg-white rounded-2xl shadow-lg border border-slate-200/60 p-6 sm:p-8">
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="firmenregistrierung-form">
            {error && <div className="bg-red-50 text-red-700 text-sm p-3 rounded-xl border border-red-200" data-testid="register-error">{error}</div>}

            <div>
              <h2 className="font-['Manrope'] text-lg font-bold text-slate-800 mb-4">Unternehmen</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2"><Field id="firmenname" label="Firmenname" required><Input id="firmenname" data-testid="reg-firmenname" required value={form.firmenname} onChange={set("firmenname")} className="h-12 rounded-xl" placeholder="Musterfirma GmbH" /></Field></div>
                <div className="sm:col-span-2"><Field id="strasse" label="Straße & Hausnummer"><Input id="strasse" data-testid="reg-strasse" value={form.strasse} onChange={set("strasse")} className="h-12 rounded-xl" /></Field></div>
                <Field id="plz" label="PLZ"><Input id="plz" data-testid="reg-plz" value={form.plz} onChange={set("plz")} className="h-12 rounded-xl" inputMode="numeric" /></Field>
                <Field id="ort" label="Ort"><Input id="ort" data-testid="reg-ort" value={form.ort} onChange={set("ort")} className="h-12 rounded-xl" /></Field>
              </div>
            </div>

            <div>
              <h2 className="font-['Manrope'] text-lg font-bold text-slate-800 mb-4">Ansprechpartner/in</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2"><Field id="ansprechpartner" label="Vor- und Nachname" required><Input id="ansprechpartner" data-testid="reg-ansprechpartner" required value={form.ansprechpartner} onChange={set("ansprechpartner")} className="h-12 rounded-xl" /></Field></div>
                <Field id="email" label="E-Mail (Login)" required><Input id="email" data-testid="reg-email" type="email" required value={form.email} onChange={set("email")} className="h-12 rounded-xl" /></Field>
                <Field id="telefon" label="Telefon"><Input id="telefon" data-testid="reg-telefon" type="tel" value={form.telefon} onChange={set("telefon")} className="h-12 rounded-xl" /></Field>
              </div>
            </div>

            <div>
              <h2 className="font-['Manrope'] text-lg font-bold text-slate-800 mb-4">Zugangsdaten</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field id="passwort" label="Passwort (min. 8 Zeichen)" required><Input id="passwort" data-testid="reg-passwort" type="password" required value={form.passwort} onChange={set("passwort")} className="h-12 rounded-xl" /></Field>
                <Field id="passwort2" label="Passwort wiederholen" required><Input id="passwort2" data-testid="reg-passwort2" type="password" required value={form.passwort2} onChange={set("passwort2")} className="h-12 rounded-xl" /></Field>
              </div>
            </div>

            <label className="flex items-start gap-3 text-sm text-slate-600 cursor-pointer" htmlFor="agb">
              <Checkbox id="agb" data-testid="reg-agb" checked={form.agb_akzeptiert} onCheckedChange={(v) => setForm((f) => ({ ...f, agb_akzeptiert: !!v }))} className="mt-0.5" />
              <span>Ich akzeptiere die Teilnahmebedingungen und habe die <Link to="/" className="text-cyan-700 underline">Datenschutzhinweise</Link> zur Kenntnis genommen. Die Bezahlung erfolgt auf Rechnung. <span className="text-red-500">*</span></span>
            </label>

            <Button type="submit" data-testid="reg-submit" disabled={loading} className="w-full bg-cyan-700 hover:bg-cyan-600 text-white rounded-xl h-12 text-base font-semibold">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Firmenzugang anlegen"}
            </Button>
            <p className="text-center text-sm text-slate-500">Bereits registriert? <Link to="/schulungen/login" className="text-cyan-700 font-semibold hover:underline" data-testid="reg-to-login-link">Zum Login</Link></p>
          </form>
        </div>
      </div>
    </div>
  );
}
